/**
 * EVAR-specific measurement binding for catalog hint computation.
 *
 * Builds a MeasurementLookup from the sizing state, indexed directly
 * by template measurement IDs (e.g., "dP2", "dP3") which are referenced
 * in catalog field definitions.
 */

import type { MeasurementLookup } from '@/workflows/vascular/common/catalog';
import type { SizingStateSnapshot } from '@/workflows/vascular/common/hooks/use-sizing-state';

/**
 * Build a MeasurementLookup from the current sizing state.
 *
 * Indexes all measurements by their template ID so catalog fields can
 * reference them directly (e.g., `"measures": ["dP2"]`).
 */
export function buildMeasurementLookup(sizing: SizingStateSnapshot | null): MeasurementLookup {
    if (!sizing) return {};

    const lookup: MeasurementLookup = {};

    const allMeasurements = [
        ...sizing.measurements.lengths,
        ...sizing.measurements.angles,
        ...sizing.measurements.derived,
        ...sizing.diameterGroups.flatMap((g) => [g.main, ...g.offsets]),
        ...sizing.minMaxDiameters,
        ...sizing.customDiameters,
    ];

    for (const m of allMeasurements) {
        lookup[m.id] = { value: m.value, unit: m.unit, label: m.shortLabel };
    }

    return lookup;
}
